import config from "../../../config";
import { CSRF_TOKEN_HEADER_KEY, getCsrfToken } from "services/AuthService";

/**
 * Service for handling all API calls related to Unprocessed Fax Details
 */
class UnprocessedFaxDetailsService {
  /**
   * Fetch unprocessed fax details
   */
  async fetchUnprocessedFaxDetails(faxInfoId) {
    const response = await fetch(
      `${config.apiRoot}/fax/unprocessed-fax-details?fax_info_id=${faxInfoId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch fax details: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Link unprocessed fax pages to switch
   */
  async linkUnprocessedFaxToSwitch(faxId, linkPages) {
    const transformedLinkPages = linkPages.map((page) => ({
      selectedGlobalSwitchId: page.identifier,
      isUpsideDown: page.isUpsideDown || false,
      pageIndex: page.referenceField ? parseInt(page.referenceField, 10) : null,
      overrideAssociatedSwitch: page.overrideAssociatedSwitch || false,
      additionalPages: page.additionalField || null,
    }));

    const response = await fetch(`${config.apiRoot}/fax/link-fax-page`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        faxInfoId: faxId,
        noOfLinkPages: transformedLinkPages.length,
        linkPages: transformedLinkPages,
      }),
      headers: {
        "Content-Type": "application/json",
        [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Failed to link fax: ${response.statusText}`);
    }

    return;
  }

  /**
   * Discard fax
   */
  async discardFax(faxInfoId) {
    const response = await fetch(
      `${config.apiRoot}/fax/discard-fax?fax_info_id=${faxInfoId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to discard fax: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Set priority of unprocessed fax
   */
  async setPriorityOfUnprocessedFax(faxId, priority) {
    const response = await fetch(
      `${config.apiRoot}/fax/unprocessed-fax-details/fax-priority?fax_info_id=${faxId}`,
      {
        method: "PUT",
        credentials: "include",
        body: priority || "",
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update priority: ${response.statusText}`);
    }

    return { success: true };
  }

  /**
   * Fetch note for unlinked fax
   */
  async fetchNoteForUnlinkedFax(faxId) {
    const response = await fetch(
      `${config.apiRoot}/fax/unprocessed-fax-details/${faxId}/get-note`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      // If note doesn't exist, return null instead of throwing
      if (response.status === 404) {
        return null;
      }
      throw new Error(`Failed to fetch note: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Save note
   */
  async saveNote(faxId, noteObject) {
    const response = await fetch(
      `${config.apiRoot}/fax/unprocessed-fax-details/${faxId}/fax-note`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify(noteObject),
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Failed to save note: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Update note
   */
  async updateNote(faxId, noteObject) {
    const response = await fetch(
      `${config.apiRoot}/fax/unprocessed-fax-details/${faxId}/fax-note`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify(noteObject),
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Failed to update note: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Delete note
   */
  async deleteNote(noteId) {
    const response = await fetch(
      `${config.apiRoot}/fax/unprocessed-fax-details/${noteId}/delete-note`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to delete note: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Fetch all available tags
   */
  async fetchAllTags() {
    const response = await fetch(`${config.apiRoot}/api/fax-tags`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tags: ${response.statusText}`);
    }

    const data = await response.json();
    return data.map((tag) => tag.faxTagId);
  }

  /**
   * Fetch fax program codes (for Unknown fax type tags)
   */
  async fetchFaxProgramTags() {
    const response = await fetch(`${config.apiRoot}/fax-program/codes`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch fax program codes: ${response.statusText}`);
    }

    const data = await response.json();
    // API returns a list of strings (codes), return as-is
    return Array.isArray(data) ? data : [];
  }

  /**
   * Fetch tags for a specific fax
   */
  async fetchFaxTags(faxInfoId) {
    const response = await fetch(
      `${config.apiRoot}/api/faxes/${faxInfoId}/tags`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      // If tags don't exist, return empty array
      if (response.status === 404) {
        return [];
      }
      throw new Error(`Failed to fetch fax tags: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Save tags for a fax
   */
  async saveTags(faxInfoId, tags) {
    const response = await fetch(
      `${config.apiRoot}/api/faxes/${faxInfoId}/tags`,
      {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ tags }),
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Failed to save tags: ${response.statusText}`);
    }

    return { success: true };
  }

  /**
   * Fetch existing linked NPIs/pages for a fax (for NON_SWITCH)
   */
  async fetchLinkedNpisForFax(faxInfoId) {
    const response = await fetch(
      `${config.apiRoot}/fax-non-switch-archive/fax-info/${faxInfoId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );

    if (!response.ok) {
      // If no linked NPIs exist, return empty array
      if (response.status === 404) {
        return [];
      }
      throw new Error(`Failed to fetch linked NPIs: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Complete linking page for NON_SWITCH fax
   */
  async completePageLinking(faxInfoId) {
    const response = await fetch(
      `${config.apiRoot}/fax/complete-linkage/${faxInfoId}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
        },
      }
    );
  
    if (!response.ok) {
      let errorMessage = `Failed to complete linking: ${response.statusText}`;
      try {
        const error = await response.json();
        errorMessage = error.message || errorMessage;
      } catch (_) {}
      throw new Error(errorMessage);
    }
  
    return;
  }
  

  /**
   * Link NPI to fax (dummy API call for NON_SWITCH workflow)
   */
  async linkAndArchiveFax(faxInfoId, linkPages, isCompletelyLinked = false) {
    // Prepare the request body with relevant data for linking NPI to fax
    const requestBody = {
      faxInfoId: faxInfoId,
      isCompletelyLinked: isCompletelyLinked,
      linkPages: linkPages.map((page) => ({
        prescriberNpi: page.identifier, // NPI identifier
        faxReferenceId: page.referenceField, // Fax Reference ID
        linkPages: page.additionalField, // Link Pages (comma-separated or array)
        isUpsideDown: page.isUpsideDown || false,
      })),
    };

    const response = await fetch(`${config.apiRoot}/fax-non-switch-archive`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify(requestBody),
      headers: {
        "Content-Type": "application/json",
        [CSRF_TOKEN_HEADER_KEY]: getCsrfToken(),
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Failed to link NPI to fax: ${response.statusText}`);
    }

    // API returns ResponseEntity.ok().build() - empty response body (200 OK with no content)
    // Try to parse JSON, but handle empty response gracefully
    try {
      const text = await response.text();
      return text ? JSON.parse(text) : { success: true };
    } catch (parseError) {
      // If parsing fails or response is empty, return success indicator
      return { success: true };
    }
  }
}

export default new UnprocessedFaxDetailsService();

